import React, { useState } from 'react';
import { userRequest } from '../../utils/requestMethods';
import FormInput from './FormInput';

const SecuritySettings = ({ userId, schoolId }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);
  const [logoutAllError, setLogoutAllError] = useState(null);
  const [logoutAllSuccess, setLogoutAllSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validation
    if (formData.newPassword !== formData.confirmPassword) {
      setError("New passwords don't match");
      return;
    }
    
    if (formData.newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      await userRequest.put("auth/change-password",
      {
        old_password: formData.currentPassword,
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword,
      });
      
      setSuccess(true);
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Reset success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutAllDevices = async () => {
    setLogoutAllError(null);
    setLogoutAllLoading(true);
    try {
      await userRequest.post("auth/logout-all-devices");
      setLogoutAllSuccess(true);
      setTimeout(() => setLogoutAllSuccess(false), 4000);
    } catch (err) {
      setLogoutAllError(err.response?.data?.message || 'Failed to logout all devices. Please try again.');
    } finally {
      setLogoutAllLoading(false);
    }
  };

  return (
    <div className="max-w-lg space-y-8">
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <FormInput
          label="Current Password"
          name="currentPassword"
          type="password"
          value={formData.currentPassword}
          onChange={handleChange}
          placeholder="Enter current password"
          required
        />
        
        <FormInput
          label="New Password"
          name="newPassword"
          type="password"
          value={formData.newPassword}
          onChange={handleChange}
          placeholder="Enter new password"
          required
        />
        
        <FormInput
          label="Confirm New Password"
          name="confirmPassword"
          type="password"
          value={formData.confirmPassword}
          onChange={handleChange}
          placeholder="Confirm new password"
          required
        />
      </div>
      
      {error && (
        <div className="mt-4 text-red-500 text-sm">{error}</div>
      )}
      
      {success && (
        <div className="mt-4 text-green-500 text-sm">Password changed successfully!</div>
      )}
      
      <div className="mt-6">
        <button
          type="submit"
          disabled={loading}
          className="bg-indigo-800 py-2 px-6 rounded-full text-white font-medium transition-all hover:shadow-lg disabled:opacity-70"
        >
          {loading ? 'Changing...' : 'Change Password'}
        </button>
      </div>
    </form>

    {/* Logout All Devices */}
    <div className="border border-red-100 rounded-2xl p-6 bg-red-50 dark:bg-red-900/10 dark:border-red-900/30">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
        </div>
        <div className="flex-1">
          <p className="font-semibold text-gray-900 dark:text-gray-100">Logout from all other devices</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Revoke access from all active sessions except your current one. Use this if you suspect unauthorised access to your account.
          </p>
          {logoutAllError && (
            <p className="mt-3 text-sm text-red-600 dark:text-red-400">{logoutAllError}</p>
          )}
          {logoutAllSuccess && (
            <p className="mt-3 text-sm text-green-600 dark:text-green-400">All other devices have been logged out.</p>
          )}
          <button
            type="button"
            onClick={handleLogoutAllDevices}
            disabled={logoutAllLoading}
            className="mt-4 py-2 px-5 rounded-full text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-60"
          >
            {logoutAllLoading ? 'Logging out...' : 'Logout all devices'}
          </button>
        </div>
      </div>
    </div>
    </div>
  );
};

export default SecuritySettings;